import {
   ANNUAL_MILEAGE_OPTIONS, CAR_INSURANCE_COVERAGE_TYPE_OPTIONS,
   CAR_OWNER_TYPE_OPTIONS,
   NCD_EARNING_OPTIONS, NCD_EARNING_PLACE_OPTIONS, NO_CLAIMS_DISCOUNT_OPTIONS,
   PARKING_DURING_NIGHT_OPTIONS, PARKING_DURING_DAY_OPTIONS,
   VAN_BODY_TYPES, VAN_DOOR_OPTIONS, VAN_ROOF_TYPE_OPTIONS, VAN_SEAT_OPTIONS, VAN_USAGE_TYPE_OPTIONS,
   VAN_WHEEL_BASE_OPTIONS, VAN_BUSINESS_TYPE_OPTIONS, VOLUNTARY_EXCESS_AMOUNT_OPTIONS,
   YES_NO_CHOICE_OPTIONS
} from "@constants/dropdown-choices";
import {isNotStringOrEmptyString} from "@utils/string";
import {
   isDateLessThanNow,
   isInLabelValueArrayAsString,
   isValidDateTimeString,
   isValidPositiveNumber
} from "@utils/validators";
import {isDateGreaterThenToday} from "@utils/date-time";

/**
 * @typedef {object} data
 * @property {string} addAdditionalComments
 * @property {string} additionalDrivers
 * @property {string} additionalDrivingQualificationDate
 * @property {string} additionalDrivingQualifications
 * @property {string} britishDrivingLicenseNumber
 * @property {string} vanAlarmsOrImmobiliser
 * @property {string} vanDoorNumber
 * @property {string} vanEngineSize
 * @property {string} vanEstimatedValue
 * @property {string} vanImpoundedWithPolice
 * @property {string} vanMakeAndModel
 * @property {string} vanParkingAtDay
 * @property {string} vanParkingAtNight
 * @property {string} vanPurchased
 * @property {string} vanPurchaseDate
 * @property {string} vanRegistrationNumber
 * @property {string} vanRegistrationYear
 * @property {string} vanSeatNumber
 * @property {string} vanTransmissionType
 * @property {string} contactEmail
 * @property {string} contactPhone
 * @property {string} currentOrPreviousInsurerName
 * @property {string} discoveryType
 * @property {string} drivingLicenseDate
 * @property {string} drivingLicenseDuration
 * @property {string} drivingLicenseType
 * @property {string} dvlaMedicalConditions
 * @property {string} estimatedAnnualMileage
 * @property {string} hasAccidentClaimsInLast5Years
 * @property {string} hasChildrenUnder16
 * @property {string} hasModifications
 * @property {string} hasMotoringConvictionsInLast5Years
 * @property {string} hasNonMotoringCriminalConvictions
 * @property {string} insuranceCoverageStartDate
 * @property {string} insuranceCoverageType
 * @property {string} insurancePaymentType
 * @property {string} insurerName
 * @property {string} isBornInUk
 * @property {string} isVanKeptOnSameAddress
 * @property {string} isVanRegistrationNumKnown
 * @property {string} isHomeOwner
 * @property {string} legalOwner
 * @property {string} mainOccupation
 * @property {string} manufacturerModel
 * @property {string} manufacturerName
 * @property {string} modificationDetails
 * @property {string} modificationType
 * @property {string} motorAccidentsList
 * @property {string} noClaimsDiscount
 * @property {string} occupationIndustry
 * @property {string} otherVehicleUsage
 * @property {string} provideDrivingLicenseNumber
 * @property {string} registeredKeeper
 * @property {string} renewalPriceOfPreviousInsurer
 * @property {string} shouldAddAdditionalDrivers
 * @property {string} termsAgreement
 * @property {string} vehicleKeeperAddress
 * @property {string} vehicleUsageType
 * @property {string} voluntaryExcessAmounts
 * @property {string} wasInsuranceDeclinedBefore
 */

export const ValidateStep2Data = (data) => {

    const msgList = [/*{
        name : 'test',
        message : 'Hold!',
    }*/];
    
    if(!isInLabelValueArrayAsString(data.isVanRegistrationNumKnown,YES_NO_CHOICE_OPTIONS)) {
    
         msgList.push({
            name: 'isVanRegistrationNumKnown',
            message: `Please select if you know the van registration number`
         });
    
    }
    
    if(data.isVanRegistrationNumKnown === 'Yes') {
       
       if(isNotStringOrEmptyString(data.vanRegistrationNumber)) {
          msgList.push({
               name: 'vanRegistrationNumber',
               message: `Please enter the van registration number`
           });
       }
       
    }
    
    if(!isInLabelValueArrayAsString(data.vanBodyType,VAN_BODY_TYPES)) {
       
       msgList.push({
            name: 'vanBodyType',
            message: `Please select the van body type`
       });
       
    }
    
    if(!isInLabelValueArrayAsString(data.vanSeatNumber,VAN_SEAT_OPTIONS)) {
       msgList.push({
            name: 'vanSeatNumber',
            message: `Please select the van seat number`
       });
    }
    
    if(!isInLabelValueArrayAsString(data.vanDoorNumber,VAN_DOOR_OPTIONS)) {
       msgList.push({
            name: 'vanDoorNumber',
            message: `Please select the van door number`
       });
    }
    
    if(!isInLabelValueArrayAsString(data.anySignOfStickers,YES_NO_CHOICE_OPTIONS)) {
       msgList.push({
            name: 'anySignOfStickers',
            message: `Please select if there are any signs or stickers`
       });
    }
    
    if(!isInLabelValueArrayAsString(data.vanRoofType,VAN_ROOF_TYPE_OPTIONS)) {
       msgList.push({
            name: 'vanRoofType',
            message: `Please select the van roof type`
       });
    }
    
    if(!isInLabelValueArrayAsString(data.vanWheelBase,VAN_WHEEL_BASE_OPTIONS)) {
       msgList.push({
            name: 'vanWheelBase',
            message: `Please select the van wheel base`
       });
    }
    
    if(isNotStringOrEmptyString(data.vanEstimatedValue)) {
       msgList.push({
            name: 'vanEstimatedValue',
            message: `Please enter the estimated value of the van`
       });
    } else if(!isValidPositiveNumber(data.vanEstimatedValue)) {
       msgList.push({
            name: 'vanEstimatedValue',
            message: `Please enter a valid estimated value of the van`
       });
    }
    
    if(!isInLabelValueArrayAsString(data.isVanImported,YES_NO_CHOICE_OPTIONS)) {
       msgList.push({
            name: 'isVanImported',
            message: `Please select if the van is imported`
       });
    }
    
    if(!isInLabelValueArrayAsString(data.vanImpoundedWithPolice,YES_NO_CHOICE_OPTIONS)) {
       msgList.push({
            name: 'vanImpoundedWithPolice',
            message: `Please select if the van is impounded with the police`
       });
    }
    
    if(!isInLabelValueArrayAsString(data.vanPurchased,YES_NO_CHOICE_OPTIONS)) {
       msgList.push({
            name: 'vanPurchased',
            message: `Please select if the van is purchased`
       });
    }
    
    if('Yes' === data.vanPurchased) {
       
       if(!isValidDateTimeString(data.vanPurchaseDate)) {
          
          msgList.push({
               name: 'vanPurchaseDate',
               message: `Please enter the date of the van purchase`
           });
          
       } else if(isDateGreaterThenToday(data.vanPurchaseDate)) {
         
          msgList.push({
               name: 'vanPurchaseDate',
               message: `Please enter a valid date of the van purchase`
           });
          
       }
    }
    
    if(!isInLabelValueArrayAsString(data.vanParkingAtDay,PARKING_DURING_DAY_OPTIONS)){
       msgList.push({
            name: 'vanParkingAtDay',
            message: `Please select the parking options during the day`
       });
    }
    
    if(!isInLabelValueArrayAsString(data.vanParkingAtNight,PARKING_DURING_NIGHT_OPTIONS)){
       msgList.push({
            name: 'vanParkingAtNight',
            message: `Please select the parking options during the night`
       })
    }
    
    if(!isInLabelValueArrayAsString(data.isVanKeptOnSameAddress,YES_NO_CHOICE_OPTIONS)) {
       msgList.push({
            name: 'isVanKeptOnSameAddress',
            message: `Please select if the van is kept on the same address`
       });
    }
    
    if(data.isVanKeptOnSameAddress === 'No') {
       
       if(isNotStringOrEmptyString(data.vehicleKeeperAddress)) {
          msgList.push({
               name: 'vehicleKeeperAddress',
               message: `Please enter the address of the van keeper`
           });
       }
       
    }
    
    if(!isInLabelValueArrayAsString(data.registeredKeeper,CAR_OWNER_TYPE_OPTIONS)) {
       msgList.push({
            name: 'registeredKeeper',
            message: `Please select the registered keeper`
       });
    }
    
    if(!isInLabelValueArrayAsString(data.legalOwner,CAR_OWNER_TYPE_OPTIONS)) {
       
       msgList.push({
            name: 'legalOwner',
            message: `Please select the legal owner`
        });
       
    }
    
    if(!isInLabelValueArrayAsString(data.carriageOfDangerousGoods,YES_NO_CHOICE_OPTIONS)) {
       msgList.push({
            name: 'carriageOfDangerousGoods',
            message: `Please select if the van carries dangerous goods`
       });
    }
    
    if(!isInLabelValueArrayAsString(data.wasInsuranceDeclinedBefore,YES_NO_CHOICE_OPTIONS)) {
       msgList.push({
            name: 'wasInsuranceDeclinedBefore',
            message: `Please select if any insurance was declined before`
       });
    }
    
    if(data.wasInsuranceDeclinedBefore === 'Yes') {
       
       if(isNotStringOrEmptyString(data.insurerName)) {
          msgList.push({
               name: 'insurerName',
               message: `Please enter the name of the insurer declining the insurance`
           });
       }
       
    }
    
    if(!isInLabelValueArrayAsString(data.vehicleUsageType,VAN_USAGE_TYPE_OPTIONS)) {
       msgList.push({
            name: 'vehicleUsageType',
            message: `Please select the vehicle usage type`
       });
    }
    
    if(!isInLabelValueArrayAsString(data.estimatedAnnualMileage,ANNUAL_MILEAGE_OPTIONS)) {
       msgList.push({
            name: 'estimatedAnnualMileage',
            message: `Please select the estimated annual mileage`
       });
    }
    
    if(!isInLabelValueArrayAsString(data.vanBusinessType,VAN_BUSINESS_TYPE_OPTIONS)) {
       
       msgList.push({
            name: 'vanBusinessType',
            message: `Please select the van business type`
        });
       
    }
    
    if(!isInLabelValueArrayAsString(data.insuranceCoverageType,CAR_INSURANCE_COVERAGE_TYPE_OPTIONS)) {
       
       msgList.push({
            name: 'insuranceCoverageType',
            message: `Please select the insurance coverage type`
        });
       
    }
    
    if(!isInLabelValueArrayAsString(data.noClaimsDiscount,NO_CLAIMS_DISCOUNT_OPTIONS)) {
       msgList.push({
            name: 'noClaimsDiscount',
            message: `Please select the no claims discount`
        });
    }
    
    if(!isInLabelValueArrayAsString(data.howNCDWasEarned,NCD_EARNING_OPTIONS)) {
       msgList.push({
            name: 'howNCDWasEarned',
            message: `Please select the how no claim discount was earned`
       });
    }

    if(!isInLabelValueArrayAsString(data.whereNCDWasEarned,NCD_EARNING_PLACE_OPTIONS)) {
       msgList.push({
            name: 'whereNCDWasEarned',
            message: `Please select if the no claim discount was earned in the UK`
       });
    }
    
    if(!isInLabelValueArrayAsString(data.protectNCDForVehicle,YES_NO_CHOICE_OPTIONS)) {
       msgList.push({
            name: 'protectNCDForVehicle',
            message: `Please select if the no claim discount should be protected for the vehicle`
       });
    }
    
    if(!isInLabelValueArrayAsString(data.voluntaryExcessAmounts,VOLUNTARY_EXCESS_AMOUNT_OPTIONS)) {
       msgList.push({
            name: 'voluntaryExcessAmounts',
            message: `Please select the voluntary excess amounts`
       });
    }
    
    if(!isValidDateTimeString(data.insuranceCoverageStartDate)) {
       msgList.push({
            name: 'insuranceCoverageStartDate',
            message: `Please enter the insurance coverage start date`
       });
    } else if(isDateLessThanNow(data.insuranceCoverageStartDate)) {
       msgList.push({
            name: 'insuranceCoverageStartDate',
            message: `Please enter a valid insurance coverage start date`
       });
    }
    
    return msgList;

}